import React, { useState, useEffect, useRef } from "react";
import { useProducts } from "../../../Containers/Products/ProductList/useHook";
import { productsHook } from "./useHook";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Info } from "lucide-react";
import Swal from "sweetalert2";
import "./style.css";

const checkImageDimension = (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = function () {
      if (this.width === 450 && this.height === 450) {
        resolve(true);
      } else {
        resolve(false);
      }
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
};

// Helper function to convert hex color to color name
const hexToColorName = (hex) => {
  const colorMap = {
    "#000000": "Black",
    "#FFFFFF": "White",
    "#FF0000": "Red",
    "#00FF00": "Green",
    "#0000FF": "Blue",
    "#FFFF00": "Yellow",
    "#FF00FF": "Magenta",
    "#00FFFF": "Cyan",
    "#808080": "Gray",
    "#800000": "Maroon",
    "#008000": "Green",
    "#000080": "Navy",
    "#808000": "Olive",
    "#800080": "Purple",
    "#008080": "Teal",
    "#C0C0C0": "Silver",
    "#FFA500": "Orange",
    "#FFC0CB": "Pink",
    "#A52A2A": "Brown",
    "#FFD700": "Gold",
  };
  return colorMap[hex.toUpperCase()] || null;
};

// Helper function to convert color name to hex code
const colorNameToHex = (colorName) => {
  if (!colorName) return null;

  const nameMap = {
    black: "#000000",
    white: "#FFFFFF",
    red: "#FF0000",
    green: "#00FF00",
    blue: "#0000FF",
    yellow: "#FFFF00",
    magenta: "#FF00FF",
    cyan: "#00FFFF",
    gray: "#808080",
    grey: "#808080",
    maroon: "#800000",
    navy: "#000080",
    olive: "#808000",
    purple: "#800080",
    teal: "#008080",
    silver: "#C0C0C0",
    orange: "#FFA500",
    pink: "#FFC0CB",
    brown: "#A52A2A",
    gold: "#FFD700",
  };

  // Normalize the color name (lowercase, trim)
  const normalizedName = colorName.trim().toLowerCase();

  // Check if it's already a hex code
  if (normalizedName.match(/^#[0-9a-f]{6}$/i)) {
    return normalizedName.toUpperCase();
  }

  // Return hex code if found, otherwise null
  return nameMap[normalizedName] || null;
};

const initialFormData = {
  categoryIds: [],
  name: "",
  customUrl: "",
  description: "",
  shortDescription: "",
  // additionalInformation: "",
  status: "true",
  colors: [],
  currency: "",
  brandId: "",
  singleVarinet: false, // New field for single variant mode
  // Product-level variant fields (for singleVarinet mode)
  weight: "",
  unit: "",
  price: "",
  dimensions: "",
  moq: "",
};

function ProductModal({
  categories,
  brands,
  showModal,
  setShowModal,
  productToEdit,
  productId,
  addProduct,
  onSuccess,
}) {
  const { getProductById } = productsHook();
  const { updateProduct, loading, error } = useProducts();

  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);

  const fileInputRefs = useRef([]);
  const variantImageInputRefs = useRef([]);
  const textureImageInputRefs = useRef([]);
  const variantNameInputRefs = useRef({});
  const hasLoadedData = useRef(false);
  const categoryDropdownRef = useRef(null);

  useEffect(() => {
    const loadProduct = async () => {
      try {
        if (showModal) {
          hasLoadedData.current = false;
        }

        if (productId && !productToEdit) {
          const dataFromApi = await getProductById(productId);
          if (dataFromApi) {
            // Check if product has variants structure to determine singleVarinet
            const isSingleVariant =
              dataFromApi.singleVarinet === true ||
              (dataFromApi.variants &&
                dataFromApi.variants.length > 0 &&
                dataFromApi.variants[0]?.name === null);

            // Extract colors from variants structure if available
            let colorsData = [];
            let productLevelVariantFields = {
              weight: "",
              unit: "",
              price: "",
              dimensions: "",
              moq: "",
            };

            if (
              dataFromApi.variants &&
              Array.isArray(dataFromApi.variants) &&
              dataFromApi.variants.length > 0
            ) {
              const firstVariant = dataFromApi.variants[0];

              // For single variant mode, extract variant fields to product level
              if (isSingleVariant) {
                productLevelVariantFields = {
                  weight: firstVariant.weight || "",
                  unit: firstVariant.unit || "",
                  price: firstVariant.price || "",
                  dimensions: firstVariant.dimension || "",
                  moq: firstVariant.moq || "",
                };
              }

              dataFromApi.variants.forEach((variant) => {
                if (variant.colors && Array.isArray(variant.colors)) {
                  variant.colors.forEach((color) => {
                    const colorValue = color.color || "";
                    let colorCodeValue = color.colorCode;
                    if (!colorCodeValue && colorValue) {
                      // If colorCode is not set, try to get it from color value
                      if (colorValue.match(/^#[0-9A-Fa-f]{6}$/i)) {
                        // It's already a hex code
                        colorCodeValue = colorValue.toUpperCase();
                      } else {
                        // Try to convert color name to hex
                        colorCodeValue =
                          colorNameToHex(colorValue) || "#000000";
                      }
                    }
                    colorsData.push({
                      id: color.id || null,
                      name: variant.name || "",
                      color: colorValue,
                      colorCode: colorCodeValue || "#000000",
                      // For single variant, variant details come from product level or first entry
                      price: isSingleVariant
                        ? variant.price || color.price || ""
                        : variant.price || color.price || "",
                      weight: isSingleVariant
                        ? variant.weight || color.weight || ""
                        : variant.weight || color.weight || "",
                      dimension: isSingleVariant
                        ? variant.dimension || color.dimension || ""
                        : variant.dimension || color.dimension || "",
                      unit: isSingleVariant
                        ? variant.unit || color.unit || ""
                        : variant.unit || color.unit || "",
                      moq: isSingleVariant
                        ? variant.moq || color.moq || ""
                        : variant.moq || color.moq || "",
                      existingImages: Array.isArray(color.images)
                        ? color.images.map((img) => img.imageUrl || img)
                        : Array.isArray(color.image)
                          ? color.image
                          : [],
                      newImageFiles: [],
                      existingVariantImage:
                        variant.variant_image || color.variant_image || null,
                      variantImageFile: null,
                      texture: color.texture === true,
                      existingTextureImage: color.texture_image || null,
                      textureImageFile: null,
                      textureCode: color.texture_code || "",
                    });
                  });
                }
              });
            } else if (Array.isArray(dataFromApi.colors)) {
              // Fallback to old structure
              colorsData = dataFromApi.colors.map((color) => {
                const colorValue = color.color || "";
                let colorCodeValue = color.colorCode;
                if (!colorCodeValue && colorValue) {
                  // If colorCode is not set, try to get it from color value
                  if (colorValue.match(/^#[0-9A-Fa-f]{6}$/i)) {
                    // It's already a hex code
                    colorCodeValue = colorValue.toUpperCase();
                  } else {
                    // Try to convert color name to hex
                    colorCodeValue = colorNameToHex(colorValue) || "#000000";
                  }
                }
                return {
                  id: color.id || null,
                  name: color.name || "",
                  color: colorValue,
                  colorCode: colorCodeValue || "#000000",
                  price: color.price || "",
                  weight: color.weight || "",
                  dimension: color.dimension || "",
                  unit: color.unit || "",
                  moq: color.moq || "",
                  existingImages: Array.isArray(color.image) ? color.image : [],
                  newImageFiles: [],
                  existingVariantImage: color.variant_image || null,
                  variantImageFile: null,
                  texture: color.texture === true,
                  existingTextureImage: color.texture_image || null,
                  textureImageFile: null,
                  textureCode: color.texture_code || "",
                };
              });
            }

            const mappedData = {
              categoryIds: Array.isArray(dataFromApi.categories)
                ? dataFromApi.categories.map((cat) => cat.categoryId || cat.id)
                : dataFromApi.category?.categoryId
                  ? [dataFromApi.category.categoryId]
                  : [],
              name: dataFromApi.name || "",
              customUrl: dataFromApi.customUrl || "",
              description: dataFromApi.description || "",
              shortDescription: dataFromApi.shortDescription || "",
              status: dataFromApi.status ? "true" : "false",
              colors: colorsData,
              currency: dataFromApi.currency || "",
              brandId: dataFromApi.brandId ? String(dataFromApi.brandId) : "",
              singleVarinet: isSingleVariant,
              ...productLevelVariantFields,
            };
            setFormData(mappedData);
            hasLoadedData.current = true;
          }
        } else if (productToEdit) {
          // Check if product has variants structure to determine singleVarinet
          const isSingleVariant =
            productToEdit.singleVarinet === true ||
            (productToEdit.variants &&
              productToEdit.variants.length > 0 &&
              productToEdit.variants[0]?.name === null);

          // Extract colors from variants structure if available
          let colorsData = [];
          let productLevelVariantFields = {
            weight: "",
            unit: "",
            price: "",
            dimensions: "",
            moq: "",
          };

          if (
            productToEdit.variants &&
            Array.isArray(productToEdit.variants) &&
            productToEdit.variants.length > 0
          ) {
            const firstVariant = productToEdit.variants[0];

            // For single variant mode, extract variant fields to product level
            if (isSingleVariant) {
              productLevelVariantFields = {
                weight: firstVariant.weight || "",
                unit: firstVariant.unit || "",
                price: firstVariant.price || "",
                dimensions: firstVariant.dimension || "",
                moq: firstVariant.moq || "",
              };
            }

            productToEdit.variants.forEach((variant) => {
              if (variant.colors && Array.isArray(variant.colors)) {
                variant.colors.forEach((color) => {
                  const colorValue = color.color || "";
                  let colorCodeValue = color.colorCode;
                  if (!colorCodeValue && colorValue) {
                    // If colorCode is not set, try to get it from color value
                    if (colorValue.match(/^#[0-9A-Fa-f]{6}$/i)) {
                      // It's already a hex code
                      colorCodeValue = colorValue.toUpperCase();
                    } else {
                      // Try to convert color name to hex
                      colorCodeValue = colorNameToHex(colorValue) || "#000000";
                    }
                  }
                  colorsData.push({
                    id: color.id || null,
                    name: variant.name || "",
                    color: colorValue,
                    colorCode: colorCodeValue || "#000000",
                    // For single variant, variant details come from product level or first entry
                    price: isSingleVariant
                      ? variant.price || color.price || ""
                      : variant.price || color.price || "",
                    weight: isSingleVariant
                      ? variant.weight || color.weight || ""
                      : variant.weight || color.weight || "",
                    dimension: isSingleVariant
                      ? variant.dimension || color.dimension || ""
                      : variant.dimension || color.dimension || "",
                    unit: isSingleVariant
                      ? variant.unit || color.unit || ""
                      : variant.unit || color.unit || "",
                    moq: isSingleVariant
                      ? variant.moq || color.moq || ""
                      : variant.moq || color.moq || "",
                    existingImages: Array.isArray(color.images)
                      ? color.images.map((img) => img.imageUrl || img)
                      : Array.isArray(color.image)
                        ? color.image
                        : [],
                    newImageFiles: [],
                    existingVariantImage:
                      variant.variant_image || color.variant_image || null,
                    variantImageFile: null,
                    texture: color.texture === true,
                    existingTextureImage: color.texture_image || null,
                    textureImageFile: null,
                    textureCode: color.texture_code || "",
                  });
                });
              }
            });
          } else if (Array.isArray(productToEdit.colors)) {
            // Fallback to old structure
            colorsData = productToEdit.colors.map((color) => {
              const colorValue = color.color || "";
              let colorCodeValue = color.colorCode;
              if (!colorCodeValue && colorValue) {
                // If colorCode is not set, try to get it from color value
                if (colorValue.match(/^#[0-9A-Fa-f]{6}$/i)) {
                  // It's already a hex code
                  colorCodeValue = colorValue.toUpperCase();
                } else {
                  // Try to convert color name to hex
                  colorCodeValue = colorNameToHex(colorValue) || "#000000";
                }
              }
              return {
                id: color.id || null,
                name: color.name || "",
                color: colorValue,
                colorCode: colorCodeValue || "#000000",
                price: color.price || "",
                weight: color.weight || "",
                dimension: color.dimension || "",
                unit: color.unit || "",
                moq: color.moq || "",
                existingImages: Array.isArray(color.image) ? color.image : [],
                newImageFiles: [],
                existingVariantImage: color.variant_image || null,
                variantImageFile: null,
                texture: color.texture === true,
                existingTextureImage: color.texture_image || null,
                textureImageFile: null,
                textureCode: color.texture_code || "",
              };
            });
          }

          const mappedData = {
            categoryIds: Array.isArray(productToEdit.categories)
              ? productToEdit.categories.map((cat) => cat.categoryId || cat.id)
              : productToEdit.category?.categoryId
                ? [productToEdit.category.categoryId]
                : [],
            name: productToEdit.name || "",
            customUrl: productToEdit.customUrl || "",
            description: productToEdit.description || "",
            shortDescription: productToEdit.shortDescription || "",
            status: productToEdit.status ? "true" : "false",
            colors: colorsData,
            currency: productToEdit.currency || "",
            brandId: productToEdit.brandId ? String(productToEdit.brandId) : "",
            singleVarinet: isSingleVariant,
            ...productLevelVariantFields,
          };
          setFormData(mappedData);
          hasLoadedData.current = true;
        } else if (
          showModal &&
          !productToEdit &&
          !productId &&
          !hasLoadedData.current
        ) {
          // When opening modal for new product, add default variant (multi-variant mode is default)
          setFormData((prev) => {
            if (prev.colors.length === 0) {
              return {
                ...prev,
                colors: [
                  {
                    name: "",
                    color: "",
                    colorCode: "#000000",
                    price: "",
                    weight: "",
                    dimension: "",
                    unit: "",
                    moq: "",
                    existingImages: [],
                    newImageFiles: [],
                    existingVariantImage: null,
                    variantImageFile: null,
                    texture: false,
                    existingTextureImage: null,
                    textureImageFile: null,
                    textureCode: "",
                  },
                ],
              };
            }
            return prev;
          });
          hasLoadedData.current = true;
        } else if (!showModal) {
          // Reset to initial form data when modal closes
          setFormData(initialFormData);
          hasLoadedData.current = false;
        }
      } catch (error) {
        console.error("Error loading product:", error);
      }
    };

    if (showModal && !hasLoadedData.current) {
      loadProduct();
    }
  }, [showModal, productId, productToEdit]);

  const handleClose = () => {
    setFormData(initialFormData);
    setValidationErrors({});
    setIsCategoryDropdownOpen(false);
    setShowModal(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCategoryChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions);
    const selectedIds = selectedOptions.map((option) => parseInt(option.value));
    setFormData((prev) => ({ ...prev, categoryIds: selectedIds }));
  };

  const toggleCategory = (categoryId) => {
    setFormData((prev) => {
      const isSelected = prev.categoryIds.includes(categoryId);
      if (isSelected) {
        return {
          ...prev,
          categoryIds: prev.categoryIds.filter((id) => id !== categoryId),
        };
      } else {
        return {
          ...prev,
          categoryIds: [...prev.categoryIds, categoryId],
        };
      }
    });
    // Clear validation error when user selects a category
    if (validationErrors.categories) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.categories;
        return newErrors;
      });
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        categoryDropdownRef.current &&
        !categoryDropdownRef.current.contains(event.target)
      ) {
        setIsCategoryDropdownOpen(false);
      }
    };

    if (isCategoryDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isCategoryDropdownOpen]);

  const handleVariantMoqChange = (index, value) => {
    // Only allow numbers starting from 1, and validate range 1-10000
    if (value === "" || (Number(value) >= 1 && Number(value) <= 10000)) {
      handleVariantChange(index, "moq", value);
    }
  };

  const handleVariantChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedColors = [...prev.colors];
      updatedColors[index] = {
        ...updatedColors[index],
        [field]: value,
      };
      return { ...prev, colors: updatedColors };
    });
  };

  const handleAddImageClick = (index) => {
    if (fileInputRefs.current[index]) {
      fileInputRefs.current[index].click();
    }
  };

  const handleColorImageChange = async (colorIndex, files) => {
    if (!files || files.length === 0) return;

    const validatedFiles = [];
    for (const file of Array.from(files)) {
      try {
        const isValid = await checkImageDimension(file);
        if (isValid) {
          validatedFiles.push(file);
        } else {
          alert(
            `Image "${file.name}" must be exactly 450x450. This file was skipped.`,
          );
        }
      } catch (err) {
        console.error("Error loading file:", file.name, err);
      }
    }

    if (validatedFiles.length > 0) {
      setFormData((prev) => {
        const updatedColors = [...prev.colors];
        const existingNewFiles = updatedColors[colorIndex].newImageFiles || [];
        const totalImages =
          (updatedColors[colorIndex].existingImages?.length || 0) +
          existingNewFiles.length +
          validatedFiles.length;

        if (totalImages > 9999) {
          alert(
            `Maximum 9999 images per variant allowed. Only ${9999 - (updatedColors[colorIndex].existingImages?.length || 0) - existingNewFiles.length} more image(s) can be added.`,
          );
          return prev;
        }

        updatedColors[colorIndex].newImageFiles = [
          ...existingNewFiles,
          ...validatedFiles,
        ];
        return { ...prev, colors: updatedColors };
      });

      // Clear validation error when images are added
      if (validationErrors[`variant_${colorIndex}_images`]) {
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[`variant_${colorIndex}_images`];
          return newErrors;
        });
      }
    }
  };

  const handleRemoveColorImage = (colorIndex, imageIndex, isExisting) => {
    setFormData((prev) => {
      const updatedColors = [...prev.colors];
      if (isExisting) {
        // Remove from existing images
        updatedColors[colorIndex].existingImages = updatedColors[
          colorIndex
        ].existingImages.filter((_, i) => i !== imageIndex);
      } else {
        // Remove from new files
        updatedColors[colorIndex].newImageFiles = updatedColors[
          colorIndex
        ].newImageFiles.filter((_, i) => i !== imageIndex);
      }
      return { ...prev, colors: updatedColors };
    });
  };

  const handleVariantImageChange = async (colorIndex, file) => {
    if (!file) return;

    try {
      const isValid = await checkImageDimension(file);
      if (!isValid) {
        alert(`Variant image "${file.name}" must be exactly 450x450.`);
        return;
      }

      setFormData((prev) => {
        const updatedColors = [...prev.colors];
        updatedColors[colorIndex] = {
          ...updatedColors[colorIndex],
          variantImageFile: file,
        };
        return { ...prev, colors: updatedColors };
      });

      // Clear validation errors when variant image is added
      if (
        validationErrors[`variant_${colorIndex}_variant_image`] ||
        validationErrors[`variant_${colorIndex}_color_or_image`]
      ) {
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[`variant_${colorIndex}_variant_image`];
          delete newErrors[`variant_${colorIndex}_color_or_image`];
          // Also clear color error if variant image is now provided
          if (
            prev[`variant_${colorIndex}_color`] &&
            prev[`variant_${colorIndex}_color`].includes(
              "when variant image is not provided",
            )
          ) {
            delete newErrors[`variant_${colorIndex}_color`];
          }
          return newErrors;
        });
      }
    } catch (err) {
      console.error("Error loading variant image:", file.name, err);
      alert(`Error loading variant image: ${file.name}`);
    }
  };

  const handleRemoveVariantImage = (colorIndex) => {
    setFormData((prev) => {
      const updatedColors = [...prev.colors];
      const variant = updatedColors[colorIndex];
      const hasColor = variant.color && variant.color.trim() !== "";

      updatedColors[colorIndex] = {
        ...updatedColors[colorIndex],
        variantImageFile: null,
        existingVariantImage: null,
      };

      // Re-validate: if no color, show error for variant image
      if (!hasColor) {
        setTimeout(() => {
          setValidationErrors((prev) => {
            const newErrors = { ...prev };
            newErrors[`variant_${colorIndex}_variant_image`] =
              "Variant image is required when color is not provided";
            return newErrors;
          });
        }, 0);
      }

      return { ...prev, colors: updatedColors };
    });
  };

  const handleTextureImageChange = (colorIndex, file) => {
    if (!file) return;
    setFormData((prev) => {
      const updatedColors = [...prev.colors];
      updatedColors[colorIndex] = {
        ...updatedColors[colorIndex],
        textureImageFile: file,
        texture: true,
      };
      return { ...prev, colors: updatedColors };
    });
  };

  const handleAddTextureImageClick = (colorIndex) => {
    if (textureImageInputRefs.current[colorIndex]) {
      textureImageInputRefs.current[colorIndex].click();
    }
  };

  const handleRemoveTextureImage = (colorIndex) => {
    setFormData((prev) => {
      const updatedColors = [...prev.colors];
      updatedColors[colorIndex] = {
        ...updatedColors[colorIndex],
        textureImageFile: null,
        existingTextureImage: null,
        texture: false,
      };
      return { ...prev, colors: updatedColors };
    });
  };

  // Helper function to group colors by variant name
  const groupColorsByVariant = (colors) => {
    if (formData.singleVarinet) {
      // Single variant mode - all colors in one group, preserve original indices
      return {
        "": colors.map((color, index) => ({ ...color, originalIndex: index })),
      };
    }
    const grouped = {};
    colors.forEach((color, index) => {
      const variantName = color.name || "";
      if (!grouped[variantName]) {
        grouped[variantName] = [];
      }
      grouped[variantName].push({ ...color, originalIndex: index });
    });
    return grouped;
  };

  // Helper function to get all unique variant names (excluding temporary IDs)
  const getUniqueVariantNames = () => {
    if (formData.singleVarinet) return [];
    const names = new Set();
    formData.colors.forEach((color) => {
      // Exclude temporary variant IDs (starting with __NEW_VARIANT_)
      if (
        color.name &&
        color.name.trim() !== "" &&
        !color.name.startsWith("__NEW_VARIANT_")
      ) {
        names.add(color.name);
      }
    });
    return Array.from(names);
  };

  // Add a new variant (in multi-variant mode) or color (in single variant mode)
  const addColor = (variantName = null) => {
    const totalColors = formData.colors.length;
    if (totalColors >= 9999) {
      alert(`Maximum 9999 color entries allowed across all variants`);
      return;
    }

    setFormData((prev) => ({
      ...prev,
      colors: [
        ...prev.colors,
        {
          name: variantName || "",
          color: "",
          colorCode: "#000000",
          price: "",
          weight: "",
          dimension: "",
          unit: "",
          moq: "",
          existingImages: [],
          newImageFiles: [],
          existingVariantImage: null,
          variantImageFile: null,
          texture: false,
          existingTextureImage: null,
          textureImageFile: null,
          textureCode: "",
        },
      ],
    }));
  };

  // Add a new variant (only in multi-variant mode)
  const addVariant = () => {
    // Count all unique variant groups (including temporary ones)
    const groupedVariants = groupColorsByVariant(formData.colors);
    const variantCount = Object.keys(groupedVariants).length;

    if (variantCount >= 9999) {
      alert("Maximum 9999 variants allowed");
      return;
    }
    // Create a unique temporary identifier for the new variant
    // This ensures each new variant appears as a separate section
    const tempVariantId = `__NEW_VARIANT_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    addColor(tempVariantId); // Add new color entry with unique temporary variant name
  };

  // Add a color to an existing variant
  const addColorToVariant = (variantName) => {
    addColor(variantName);
  };

  const removeColor = (index) => {
    setFormData((prev) => {
      const updatedColors = prev.colors.filter((_, i) => i !== index);
      return { ...prev, colors: updatedColors };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate variants
    const errors = {};

    // Check if at least one category is selected
    if (!formData.categoryIds || formData.categoryIds.length === 0) {
      errors.categories = "At least one category is required";
      setValidationErrors(errors);
      setIsSubmitting(false);
      return;
    }

    // Check if at least one variant exists
    if (!formData.colors || formData.colors.length === 0) {
      errors.variants = "At least one product variant is required";
      setValidationErrors(errors);
      setIsSubmitting(false);
      return;
    }

    // Validate each variant
    formData.colors.forEach((variant, index) => {
      // Variant name is only required when singleVarinet is false
      if (
        !formData.singleVarinet &&
        (!variant.name || variant.name.trim() === "")
      ) {
        errors[`variant_${index}_name`] = "Variant name is required";
      }

      // Validate that either color OR variant_image is provided (only in multi-variant mode)
      // In single variant mode, only color is required (no variant image)
      const hasColor = variant.color && variant.color.trim() !== "";
      const hasVariantImage =
        variant.variantImageFile || variant.existingVariantImage;

      if (formData.singleVarinet) {
        // Single variant mode: only color is required
        if (!hasColor) {
          errors[`variant_${index}_color`] = "Color is required";
        }
      } else {
        // Multi-variant mode: either color OR variant_image is required
        if (!hasColor && !hasVariantImage) {
          errors[`variant_${index}_color_or_image`] =
            "Either color or variant image is required";
          errors[`variant_${index}_color`] =
            "Color is required when variant image is not provided";
          errors[`variant_${index}_variant_image`] =
            "Variant image is required when color is not provided";
        } else if (!hasColor) {
          // If no color, variant_image must be provided (already checked above)
          if (!hasVariantImage) {
            errors[`variant_${index}_variant_image`] =
              "Variant image is required when color is not provided";
          }
        } else if (!hasVariantImage) {
          // If no variant_image, color must be provided (already checked above)
          if (!hasColor) {
            errors[`variant_${index}_color`] =
              "Color is required when variant image is not provided";
          }
        }
      }

      const totalImages =
        (variant.existingImages?.length || 0) +
        (variant.newImageFiles?.length || 0);
      if (totalImages === 0) {
        errors[`variant_${index}_images`] =
          "At least one image is required for this variant";
      }
    });

    // If there are validation errors, show them and stop submission
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setIsSubmitting(false);
      // Scroll to first error
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey.startsWith("variant_")) {
        const variantIndex = parseInt(firstErrorKey.split("_")[1]);
        const variantElement = document.querySelector(
          `[data-variant-index="${variantIndex}"]`,
        );
        if (variantElement) {
          variantElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }
      return;
    }

    // Clear validation errors
    setValidationErrors({});
    setIsSubmitting(true);

    const data = new FormData();

    // Append main product fields
    Object.keys(formData).forEach((key) => {
      if (key === "colors" || key === "video") {
        // Skip colors and video, handle separately
        return;
      }
      if (key === "categoryIds") {
        // Send categoryIds as JSON array
        if (formData.categoryIds && formData.categoryIds.length > 0) {
          data.append("categoryIds", JSON.stringify(formData.categoryIds));
        }
        return;
      }
      if (key === "status") {
        // For new products, always send "true", for updates use the form value
        if (productId || productToEdit) {
          data.append(key, formData[key]);
        } else {
          data.append(key, "true");
        }
        return;
      }
      if (key === "singleVarinet") {
        // Send singleVarinet as string 'true' or 'false'
        data.append(key, formData[key] ? "true" : "false");
        return;
      }
      // For single variant mode, include product-level variant fields (weight, unit, price, dimensions, moq)
      // These will be used if provided at product level, otherwise first color entry values will be used
      if (
        formData.singleVarinet &&
        (key === "weight" ||
          key === "unit" ||
          key === "price" ||
          key === "dimensions" ||
          key === "moq")
      ) {
        if (
          formData[key] !== "" &&
          formData[key] !== null &&
          formData[key] !== undefined
        ) {
          data.append(key, formData[key]);
        }
        return;
      }
      if (
        formData[key] !== "" &&
        formData[key] !== null &&
        formData[key] !== undefined
      ) {
        data.append(key, formData[key]);
      }
    });

    // Append video file if one is selected
    if (formData.video) {
      data.append("video", formData.video);
    }

    // Handle variants (colors) - Send as JSON string
    if (formData.colors && formData.colors.length > 0) {
      const variantsData = formData.colors.map((variant, index) => {
        const variantObj = {
          // For singleVarinet mode, name should be null or omitted
          // For multi-variant mode, name is required
          name: formData.singleVarinet ? null : variant.name || null,
          color: variant.color || null,
          texture: variant.texture === true,
          texture_code: variant.textureCode || null,
        };

        // For single variant mode, variant details come from product level OR first color entry
        // For multi-variant mode, variant details come from each color entry
        if (formData.singleVarinet) {
          // Use product-level fields if available, otherwise use first color entry fields
          if (index === 0) {
            // First entry can have variant details (from product level or itself)
            variantObj.price = formData.price
              ? parseFloat(formData.price)
              : variant.price
                ? parseFloat(variant.price)
                : null;
            variantObj.weight = formData.weight
              ? parseFloat(formData.weight)
              : variant.weight
                ? parseFloat(variant.weight)
                : null;
            variantObj.dimension =
              formData.dimensions || variant.dimension || null;
            variantObj.unit = formData.unit || variant.unit || null;
            variantObj.moq = formData.moq
              ? parseInt(formData.moq)
              : variant.moq
                ? parseInt(variant.moq)
                : null;
          }
          // For other entries in single variant mode, don't include variant details
        } else {
          // Multi-variant mode: variant details come from each entry
          variantObj.price = variant.price ? parseFloat(variant.price) : null;
          variantObj.weight = variant.weight
            ? parseFloat(variant.weight)
            : null;
          variantObj.dimension = variant.dimension || null;
          variantObj.unit = variant.unit || null;
          variantObj.moq = variant.moq ? parseInt(variant.moq) : null;
        }

        // For updates, include variant ID and existing images
        if (productId || productToEdit) {
          // Include variant ID if it exists (for existing variants)
          if (variant.id) {
            variantObj.id = variant.id;
          }
          variantObj.images = variant.existingImages || [];
          // Include existing variant_image if not replacing
          if (variant.existingVariantImage && !variant.variantImageFile) {
            variantObj.variant_image = variant.existingVariantImage;
          }
          // Include existing texture_image if texture is true and not replacing with new file
          if (
            variant.texture &&
            variant.existingTextureImage &&
            !variant.textureImageFile
          ) {
            variantObj.texture_image = variant.existingTextureImage;
          }
        }

        return variantObj;
      });

      data.append("colors", JSON.stringify(variantsData));

      // Upload variant_image files using the pattern colors[${index}][variant_image]
      formData.colors.forEach((variant, index) => {
        if (variant.variantImageFile) {
          data.append(
            `colors[${index}][variant_image]`,
            variant.variantImageFile,
          );
        }
      });

      // Upload variant images using the pattern colors[${index}][images]
      formData.colors.forEach((variant, index) => {
        if (variant.newImageFiles && variant.newImageFiles.length > 0) {
          variant.newImageFiles.forEach((imageFile) => {
            data.append(`colors[${index}][images]`, imageFile);
          });
        }
      });

      // Upload texture_image file per color using the pattern colors[${index}][texture_image]
      formData.colors.forEach((variant, index) => {
        if (variant.textureImageFile) {
          data.append(
            `colors[${index}][texture_image]`,
            variant.textureImageFile,
          );
        }
      });
    } else {
      // Empty array to remove all variants on update
      data.append("colors", JSON.stringify([]));
    }

    try {
      if (productId || productToEdit) {
        const updateId = productId || productToEdit.id;
        await updateProduct(updateId, data);
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Product updated successfully",
          showConfirmButton: true,
          timer: 2000,
          timerProgressBar: true,
        });
        if (typeof onSuccess === "function") {
          onSuccess();
        }
        handleClose();
      } else {
        const response = await addProduct(data);
        if (typeof onSuccess === "function") {
          onSuccess();
        }
        handleClose();
      }
    } catch (err) {
      console.error("Error submitting product:", err);
      alert("Error submitting product: " + (err.message || "Unknown error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!showModal) return null;
  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg w-3/4 max-w-6xl relative max-h-[90vh] overflow-y-auto">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <button
          className="absolute top-2 right-2 text-gray-500 hover:text-gray-800 text-5xl"
          onClick={handleClose}
        >
          ×
        </button>

        <h2 className="text-xl font-semibold mb-4">
          {productId || productToEdit ? "Edit Product" : "Add Product"}
        </h2>

        {/* Warning message when no categories or brands are available */}
        {((Array.isArray(categories) && categories.length === 0) ||
          (Array.isArray(brands) && brands.length === 0)) && (
          <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
            <div className="flex items-center">
              <svg
                className="w-5 h-5 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="font-medium">Setup Required:</span>
            </div>
            <ul className="mt-2 ml-7 list-disc list-inside text-sm">
              {Array.isArray(categories) && categories.length === 0 && (
                <li>No categories available. Please add categories first.</li>
              )}
              {Array.isArray(brands) && brands.length === 0 && (
                <li>No brands available. Please add brands first.</li>
              )}
            </ul>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-4">
            {/* Categories */}
            {/* Categories */}
            <div className="col-span-2 relative" ref={categoryDropdownRef}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Categories <span className="text-red-500">*</span>
              </label>

              <div className="flex items-start gap-3">
                <button
                  type="button"
                  onClick={() =>
                    setIsCategoryDropdownOpen(!isCategoryDropdownOpen)
                  }
                  className={`w-64 p-2 border rounded text-left bg-white flex items-center justify-between flex-shrink-0 ${
                    validationErrors.categories
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                >
                  <span
                    className={
                      formData.categoryIds.length
                        ? "text-gray-800"
                        : "text-gray-500"
                    }
                  >
                    {formData.categoryIds.length
                      ? `${formData.categoryIds.length} categories selected`
                      : "Select categories"}
                  </span>
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform ${
                      isCategoryDropdownOpen ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {formData.categoryIds.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2 flex-1 border border-gray-200 rounded-md px-2 py-2 bg-gray-50 min-h-[42px]">
                    {formData.categoryIds.map((catId) => {
                      const category = categories.find(
                        (cat) => cat.id === catId,
                      );
                      if (!category) return null;

                      return (
                        <span
                          key={catId}
                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 whitespace-nowrap"
                        >
                          {category.name}
                          <button
                            type="button"
                            onClick={() => toggleCategory(catId)}
                            className="ml-2 text-blue-700 hover:text-blue-900 font-bold"
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>

              {isCategoryDropdownOpen && (
                <div
                  className="absolute z-50 w-64 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  {categories.map((category) => (
                    <label
                      key={category.id}
                      className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={formData.categoryIds.includes(category.id)}
                        onChange={() => toggleCategory(category.id)}
                        className="w-4 h-4"
                      />
                      <span className="ml-3 text-sm">{category.name}</span>
                    </label>
                  ))}
                </div>
              )}

              {validationErrors.categories && (
                <p className="text-red-500 text-xs mt-1">
                  {validationErrors.categories}
                </p>
              )}
            </div>

            {/* Product Name */}
            <div>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Product Name"
                className="w-full p-2 border border-gray-300 rounded"
                required
              />
            </div>
            {/* Brands */}
            <div>
              <select
                name="brandId"
                value={formData.brandId}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded"
              >
                <option value="">Select Brand</option>
                {Array.isArray(brands) && brands.length > 0 ? (
                  brands.map((brand) => (
                    <option key={brand.id} value={String(brand.id)}>
                      {brand.name}
                    </option>
                  ))
                ) : (
                  <option disabled>No brands added yet</option>
                )}
              </select>
            </div>
            {/* Description (full width) */}
            <div className="col-span-2">
              <ReactQuill
                theme="snow"
                value={formData.description}
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, description: value }))
                }
                placeholder="Description"
                className="custom-quill"
              />
            </div>
            {/* Short Description */}
            <div className="col-span-2">
              <textarea
                type="text"
                name="shortDescription"
                value={formData.shortDescription}
                onChange={handleChange}
                placeholder="Short Description"
                className="w-full p-2 border border-gray-300 rounded"
              />
            </div>
            {/* Additional Information (full width)
            <div className="col-span-2">
              <textarea
                name="additionalInformation"
                value={formData.additionalInformation}
                onChange={handleChange}
                placeholder="Additional Information"
                className="w-full p-2 border border-gray-300 rounded"
              />
            </div> */}
            {/* Status - Only show when editing */}
            {(productId || productToEdit) && (
              <div>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded"
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
            )}
            {/* Currency */}
            <div>
              <select
                name="currency"
                value={formData.currency}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded"
                required
              >
                <option value="">Select Currency</option>
                <option value="$">$</option>
                <option value="PKR">PKR</option>
              </select>
            </div>
            {/* Custom Url */}
            <div>
              <input
                type="text"
                name="customUrl"
                value={formData.customUrl}
                onChange={handleChange}
                placeholder="Custom Url"
                className="w-full p-2 border border-gray-300 rounded"
              />
            </div>
            {/* Single Variant Toggle */}
            <div className="col-span-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.singleVarinet}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      singleVarinet: e.target.checked,
                    }));
                    // Clear variant name validation errors when toggling
                    setValidationErrors((prev) => {
                      const newErrors = { ...prev };
                      Object.keys(newErrors).forEach((key) => {
                        if (
                          key.startsWith("variant_") &&
                          key.endsWith("_name")
                        ) {
                          delete newErrors[key];
                        }
                      });
                      return newErrors;
                    });
                  }}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Single Variant Mode
                </span>
                <div className="relative group flex items-center">
                  <Info className="w-4 h-4 text-gray-500 cursor-pointer" />
                  <div className="absolute z-10 hidden group-hover:flex items-center bg-gray-800 text-white text-xs rounded py-1 px-2 left-full ml-2 whitespace-nowrap max-w-xs">
                    When enabled, creates one variant with null name. Multiple
                    colors can be added to this single variant. Variant name
                    field will be hidden.
                  </div>
                </div>
              </label>
            </div>

            {/* Product-level Variant Fields (shown only when singleVarinet is true) */}
            {formData.singleVarinet && (
              <div className="col-span-2 border-t pt-4 mt-2">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  Colors Details
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {/* Weight */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Weight (in kg)
                    </label>
                    <input
                      type="number"
                      name="weight"
                      value={formData.weight}
                      onChange={handleChange}
                      placeholder="Weight"
                      className="w-full p-2 border border-gray-300 rounded text-sm"
                    />
                  </div>

                  {/* Dimension */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Dimension
                    </label>
                    <input
                      type="text"
                      name="dimensions"
                      value={formData.dimensions}
                      onChange={handleChange}
                      placeholder="e.g., 12.5 kg"
                      className="w-full p-2 border border-gray-300 rounded text-sm"
                    />
                  </div>

                  {/* Price */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Price
                    </label>
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      placeholder="Variant price"
                      className="w-full p-2 border border-gray-300 rounded text-sm"
                    />
                  </div>

                  {/* Unit */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Unit
                    </label>
                    <select
                      name="unit"
                      value={formData.unit}
                      onChange={handleChange}
                      className="w-full p-2 border border-gray-300 rounded text-sm"
                    >
                      <option value="">Select Unit</option>
                      <option value="per_can">Per Can</option>
                      <option value="per_piece">Per Piece</option>
                      <option value="per_bag">Per Bag</option>
                      <option value="per_roll">Per Roll</option>
                      <option value="per_bottle">Per Bottle</option>
                      <option value="running_feet">Running Feet</option>
                      <option value="per_drum">Per Drum</option>
                      <option value="per_quarter">Per Quarter</option>
                      <option value="per_square_feet">Per Square Feet</option>
                      <option value="per_cubic_feet">Per Cubic Feet</option>
                      <option value="per_kit">Per Kit</option>
                      <option value="per_sheet">Per Sheet</option>
                      <option value="per_pair">Per Pair</option>
                      <option value="per_set">Per Set</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
            {/* Video Upload
            <div className="col-span-2">
              <label className="block mb-2 text-sm font-semibold text-gray-700">
                Upload Video
              </label>
              <div className="w-full p-3 border rounded-xl border-gray-300 bg-white shadow-sm">
                <input
                  type="file"
                  name="video"
                  accept="video/*"
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      video: e.target.files[0],
                    }))
                  }
                  className="w-full cursor-pointer file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-100 file:text-blue-700 hover:file:bg-blue-200 transition duration-200"
                />
                {formData.video && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-700">
                      {formData.video?.name}
                    </p>
                    <video
                      src={URL.createObjectURL(formData.video)}
                      controls
                      className="mt-2 w-full rounded-lg shadow-md"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, video: null }))
                      }
                      className="mt-2 text-sm text-red-600 hover:text-red-800 transition duration-200"
                    >
                      Remove Video
                    </button>
                  </div>
                )}
              </div>
            </div>
 */}
            {/* Variants (Colors) & Images Section */}
            <div className="col-span-2">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="font-semibold">
                  {formData.singleVarinet
                    ? "Product Colors"
                    : "Product Variants"}{" "}
                  <span className="text-red-500">*</span>
                </h3>
                <div className="relative group flex items-center">
                  <Info className="w-4 h-4 text-gray-500 cursor-pointer" />
                  <div className="absolute z-10 hidden group-hover:flex items-center bg-gray-800 text-white text-xs rounded py-1 px-2 left-full ml-2 whitespace-nowrap max-w-xs">
                    {formData.singleVarinet
                      ? "Each color requires: either color name OR variant image (450×450), and multiple product images (450×450). Variant details can be set at product level or in first color entry."
                      : "Each variant requires: Variant name (required), either color OR variant image (450×450), and multiple product images (450×450)."}
                  </div>
                </div>
              </div>

              {/* Validation Error for categories */}
              {validationErrors.categories && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  {validationErrors.categories}
                </div>
              )}

              {/* Validation Error for no variants */}
              {validationErrors.variants && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  {validationErrors.variants}
                </div>
              )}

              {/* Render variants grouped by name (multi-variant mode) or flat list (single variant mode) */}
              {(() => {
                const groupedVariants = groupColorsByVariant(formData.colors);
                const variantNames = Object.keys(groupedVariants);

                return variantNames.map((variantName, variantGroupIndex) => {
                  const colorsInVariant = groupedVariants[variantName];
                  // Check if this is a temporary new variant ID
                  const isTempVariant =
                    variantName && variantName.startsWith("__NEW_VARIANT_");
                  const displayVariantName = isTempVariant
                    ? ""
                    : variantName || "";

                  return (
                    <div
                      key={variantName || `single-variant-${variantGroupIndex}`}
                      className="mb-6 border-2 border-blue-200 rounded-2xl shadow-lg p-5 bg-gradient-to-br from-blue-50 to-white"
                    >
                      {/* Variant Header (only in multi-variant mode) */}
                      {!formData.singleVarinet && (
                        <div className="mb-4 pb-3 border-b-2 border-blue-300">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex-1">
                              <h4 className="font-bold text-lg text-gray-800 mb-2">
                                Variant:{" "}
                                {displayVariantName || "Unnamed Variant"}
                                <span className="text-sm font-normal text-gray-600 ml-2">
                                  ({colorsInVariant.length}{" "}
                                  {colorsInVariant.length === 1
                                    ? "color"
                                    : "colors"}
                                  )
                                </span>
                              </h4>
                              {/* Variant Name Input */}
                              <div className="mb-3">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Variant Name{" "}
                                  <span className="text-red-500">*</span>
                                </label>
                                <input
                                  type="text"
                                  key={`variant-name-${variantName}`}
                                  value={displayVariantName}
                                  ref={(el) => {
                                    if (
                                      el &&
                                      colorsInVariant[0]?.originalIndex !==
                                        undefined
                                    ) {
                                      variantNameInputRefs.current[
                                        colorsInVariant[0].originalIndex
                                      ] = el;
                                    }
                                  }}
                                  onChange={(e) => {
                                    // Store cursor position and selection
                                    const input = e.target;
                                    const cursorPosition = input.selectionStart;
                                    const selectionEnd = input.selectionEnd;

                                    // Update all colors with the same variant name
                                    const newName = e.target.value;
                                    setFormData((prev) => {
                                      const updatedColors = prev.colors.map(
                                        (c, idx) => {
                                          if (c.name === variantName) {
                                            return { ...c, name: newName };
                                          }
                                          return c;
                                        },
                                      );
                                      return { ...prev, colors: updatedColors };
                                    });

                                    // Restore cursor position after state update
                                    requestAnimationFrame(() => {
                                      const firstColorIndex =
                                        colorsInVariant[0]?.originalIndex;
                                      if (firstColorIndex !== undefined) {
                                        const inputRef =
                                          variantNameInputRefs.current[
                                            firstColorIndex
                                          ];
                                        if (
                                          inputRef &&
                                          document.activeElement !== inputRef
                                        ) {
                                          inputRef.focus();
                                          inputRef.setSelectionRange(
                                            cursorPosition,
                                            selectionEnd,
                                          );
                                        }
                                      }
                                    });

                                    // Clear validation error when user types
                                    const firstColorIndex =
                                      colorsInVariant[0]?.originalIndex;
                                    if (
                                      firstColorIndex !== undefined &&
                                      validationErrors[
                                        `variant_${firstColorIndex}_name`
                                      ]
                                    ) {
                                      setValidationErrors((prev) => {
                                        const newErrors = { ...prev };
                                        delete newErrors[
                                          `variant_${firstColorIndex}_name`
                                        ];
                                        return newErrors;
                                      });
                                    }
                                  }}
                                  placeholder="e.g., 12.5 kg Kit"
                                  className={`w-full p-2 border rounded text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none ${
                                    colorsInVariant[0]?.originalIndex !==
                                      undefined &&
                                    validationErrors[
                                      `variant_${colorsInVariant[0].originalIndex}_name`
                                    ]
                                      ? "border-red-500"
                                      : "border-gray-300"
                                  }`}
                                  required
                                />
                                {colorsInVariant[0]?.originalIndex !==
                                  undefined &&
                                  validationErrors[
                                    `variant_${colorsInVariant[0].originalIndex}_name`
                                  ] && (
                                    <p className="text-red-500 text-xs mt-1">
                                      {
                                        validationErrors[
                                          `variant_${colorsInVariant[0].originalIndex}_name`
                                        ]
                                      }
                                    </p>
                                  )}
                              </div>
                            </div>
                            {/* Remove Variant Button - Only show if there's more than one variant */}
                            {variantNames.length > 1 && (
                              <button
                                type="button"
                                onClick={() => {
                                  // Remove all colors in this variant
                                  const indicesToRemove = colorsInVariant
                                    .map((c) => c.originalIndex)
                                    .sort((a, b) => b - a);
                                  setFormData((prev) => {
                                    let updatedColors = [...prev.colors];
                                    indicesToRemove.forEach((idx) => {
                                      updatedColors = updatedColors.filter(
                                        (_, i) => i !== idx,
                                      );
                                    });
                                    return { ...prev, colors: updatedColors };
                                  });
                                }}
                                className="bg-red-100 text-red-700 font-semibold shadow-md hover:bg-red-200 hover:text-red-800 px-3 py-1.5 rounded-lg text-sm ml-4"
                              >
                                Remove Variant
                              </button>
                            )}
                          </div>

                          {/* Variant Image - One per variant (independent of colors) */}
                          {(() => {
                            const firstColorIndex =
                              colorsInVariant[0]?.originalIndex;
                            const firstColor =
                              firstColorIndex !== undefined
                                ? formData.colors[firstColorIndex]
                                : null;
                            const variantImageFile =
                              firstColor?.variantImageFile;
                            const existingVariantImage =
                              firstColor?.existingVariantImage;

                            return (
                              <div className="mb-3">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Variant Image{" "}
                                  <span className="text-xs text-gray-500">
                                    (450×450)
                                  </span>
                                </label>
                                <div className="flex items-center gap-3">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    style={{ display: "none" }}
                                    ref={(el) => {
                                      if (el && firstColorIndex !== undefined) {
                                        variantImageInputRefs.current[
                                          `variant_${variantName}`
                                        ] = el;
                                      }
                                    }}
                                    onChange={(e) => {
                                      if (
                                        e.target.files &&
                                        e.target.files[0] &&
                                        firstColorIndex !== undefined
                                      ) {
                                        handleVariantImageChange(
                                          firstColorIndex,
                                          e.target.files[0],
                                          variantName,
                                        );
                                      }
                                    }}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const inputRef =
                                        variantImageInputRefs.current[
                                          `variant_${variantName}`
                                        ];
                                      if (inputRef) {
                                        inputRef.click();
                                      }
                                    }}
                                    className="bg-blue-100 text-blue-700 font-semibold shadow-md hover:bg-blue-200 hover:text-blue-800 focus:bg-blue-200 focus:text-blue-800 focus:font-semibold focus:shadow-lg transition duration-200 px-3 py-1.5 rounded-lg text-sm"
                                  >
                                    {variantImageFile || existingVariantImage
                                      ? "Change Variant Image"
                                      : "Upload Variant Image"}
                                  </button>
                                  {(variantImageFile ||
                                    existingVariantImage) && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        if (firstColorIndex !== undefined) {
                                          handleRemoveVariantImage(
                                            firstColorIndex,
                                            variantName,
                                          );
                                        }
                                      }}
                                      className="bg-red-100 text-red-600 font-semibold shadow-md hover:bg-red-200 hover:text-red-700 focus:bg-red-200 focus:text-red-800 focus:font-semibold focus:shadow-lg transition duration-200 px-3 py-1.5 rounded-lg text-sm"
                                    >
                                      Remove Variant Image
                                    </button>
                                  )}
                                </div>
                                {(variantImageFile || existingVariantImage) && (
                                  <div className="mt-3">
                                    <img
                                      src={
                                        variantImageFile
                                          ? URL.createObjectURL(
                                              variantImageFile,
                                            )
                                          : existingVariantImage
                                      }
                                      alt="Variant preview"
                                      className="w-32 h-32 object-cover rounded border border-gray-300"
                                      onError={(e) => {
                                        e.target.src = "/assets/logo.png";
                                        e.target.onerror = null;
                                      }}
                                    />
                                    {variantImageFile && (
                                      <p className="text-xs text-gray-600 mt-1">
                                        {variantImageFile.name}
                                      </p>
                                    )}
                                    {existingVariantImage &&
                                      !variantImageFile && (
                                        <p className="text-xs text-green-600 mt-1">
                                          Existing image
                                        </p>
                                      )}
                                  </div>
                                )}
                              </div>
                            );
                          })()}

                          {/* Variant Details - At variant level (like single variant mode at product level) */}
                          {(() => {
                            const firstColorIndex =
                              colorsInVariant[0]?.originalIndex;
                            const firstColor =
                              firstColorIndex !== undefined
                                ? formData.colors[firstColorIndex]
                                : null;

                            return (
                              <div className="mt-4 pt-3 border-t border-gray-300">
                                <h5 className="text-sm font-semibold text-gray-700 mb-3">
                                  Variant Details
                                </h5>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                  {/* Weight */}
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Weight (in kg)
                                    </label>
                                    <input
                                      type="number"
                                      value={firstColor?.weight || ""}
                                      onChange={(e) => {
                                        if (firstColorIndex !== undefined) {
                                          // Update all colors with the same variant name
                                          const newWeight = e.target.value;
                                          setFormData((prev) => {
                                            const updatedColors =
                                              prev.colors.map((c, idx) => {
                                                if (c.name === variantName) {
                                                  return {
                                                    ...c,
                                                    weight: newWeight,
                                                  };
                                                }
                                                return c;
                                              });
                                            return {
                                              ...prev,
                                              colors: updatedColors,
                                            };
                                          });
                                        }
                                      }}
                                      placeholder="Weight"
                                      className="w-full p-2 border border-gray-300 rounded text-sm"
                                    />
                                  </div>

                                  {/* Dimension */}
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Dimension
                                    </label>
                                    <input
                                      type="text"
                                      value={firstColor?.dimension || ""}
                                      onChange={(e) => {
                                        if (firstColorIndex !== undefined) {
                                          const newDimension = e.target.value;
                                          setFormData((prev) => {
                                            const updatedColors =
                                              prev.colors.map((c, idx) => {
                                                if (c.name === variantName) {
                                                  return {
                                                    ...c,
                                                    dimension: newDimension,
                                                  };
                                                }
                                                return c;
                                              });
                                            return {
                                              ...prev,
                                              colors: updatedColors,
                                            };
                                          });
                                        }
                                      }}
                                      placeholder="e.g., 12.5 kg"
                                      className="w-full p-2 border border-gray-300 rounded text-sm"
                                    />
                                  </div>

                                  {/* Price */}
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Price
                                    </label>
                                    <input
                                      type="number"
                                      value={firstColor?.price || ""}
                                      onChange={(e) => {
                                        if (firstColorIndex !== undefined) {
                                          const newPrice = e.target.value;
                                          setFormData((prev) => {
                                            const updatedColors =
                                              prev.colors.map((c, idx) => {
                                                if (c.name === variantName) {
                                                  return {
                                                    ...c,
                                                    price: newPrice,
                                                  };
                                                }
                                                return c;
                                              });
                                            return {
                                              ...prev,
                                              colors: updatedColors,
                                            };
                                          });
                                        }
                                      }}
                                      placeholder="Variant price"
                                      className="w-full p-2 border border-gray-300 rounded text-sm"
                                    />
                                  </div>

                                  {/* Unit */}
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Unit
                                    </label>
                                    <select
                                      value={firstColor?.unit || ""}
                                      onChange={(e) => {
                                        if (firstColorIndex !== undefined) {
                                          const newUnit = e.target.value;
                                          setFormData((prev) => {
                                            const updatedColors =
                                              prev.colors.map((c, idx) => {
                                                if (c.name === variantName) {
                                                  return {
                                                    ...c,
                                                    unit: newUnit,
                                                  };
                                                }
                                                return c;
                                              });
                                            return {
                                              ...prev,
                                              colors: updatedColors,
                                            };
                                          });
                                        }
                                      }}
                                      className="w-full p-2 border border-gray-300 rounded text-sm"
                                    >
                                      <option value="">Select Unit</option>
                                      <option value="per_can">Per Can</option>
                                      <option value="per_piece">
                                        Per Piece
                                      </option>
                                      <option value="per_bag">Per Bag</option>
                                      <option value="per_roll">Per Roll</option>
                                      <option value="per_bottle">
                                        Per Bottle
                                      </option>
                                      <option value="running_feet">
                                        Running Feet
                                      </option>
                                      <option value="per_drum">Per Drum</option>
                                      <option value="per_quarter">
                                        Per Quarter
                                      </option>
                                      <option value="per_square_feet">
                                        Per Square Feet
                                      </option>
                                      <option value="per_cubic_feet">
                                        Per Cubic Feet
                                      </option>
                                      <option value="per_kit">Per Kit</option>
                                      <option value="per_sheet">
                                        Per Sheet
                                      </option>
                                      <option value="per_pair">Per Pair</option>
                                      <option value="per_set">Per Set</option>
                                    </select>
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      )}

                      {/* Colors in this variant */}
                      {colorsInVariant.map((colorData, colorIndexInVariant) => {
                        const actualIndex =
                          colorData.originalIndex !== undefined
                            ? colorData.originalIndex
                            : formData.singleVarinet
                              ? colorIndexInVariant
                              : formData.colors.findIndex((c, idx) => {
                                  if (formData.singleVarinet) return false;
                                  const matchingColors = formData.colors.filter(
                                    (cc) => cc.name === variantName,
                                  );
                                  return (
                                    c.name === variantName &&
                                    matchingColors.indexOf(c) ===
                                      colorIndexInVariant
                                  );
                                });
                        const variant = formData.colors[actualIndex];
                        const allImages = [
                          ...(variant.existingImages || []),
                          ...(variant.newImageFiles || []),
                        ];
                        const isFirstColor = colorIndexInVariant === 0;

                        return (
                          <div
                            key={actualIndex}
                            data-variant-index={actualIndex}
                            className={`mb-4 ${colorIndexInVariant < colorsInVariant.length - 1 ? "border-b border-gray-300 pb-4" : ""}`}
                          >
                            <div className="mb-4">
                              <h5 className="font-semibold text-gray-700 mb-3 text-sm">
                                {formData.singleVarinet
                                  ? `Color ${actualIndex + 1}`
                                  : `Color ${colorIndexInVariant + 1}`}
                              </h5>

                              {/* Color Input Field with Color Picker - Show in both single and multi-variant modes */}
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                                <div className="md:col-span-2">
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Color{" "}
                                    {formData.singleVarinet && (
                                      <span className="text-red-500">*</span>
                                    )}
                                  </label>
                                  <div className="flex gap-2 items-center">
                                    {/* Color Picker */}
                                    <div className="relative">
                                      <input
                                        type="color"
                                        value={(() => {
                                          // Get colorCode if available
                                          if (variant.colorCode) {
                                            return variant.colorCode;
                                          }
                                          // Try to get hex from color name
                                          const hexFromName = colorNameToHex(
                                            variant.color,
                                          );
                                          if (hexFromName) {
                                            return hexFromName;
                                          }
                                          // Check if color is already a hex code
                                          if (
                                            variant.color &&
                                            variant.color.match(
                                              /^#[0-9A-Fa-f]{6}$/i,
                                            )
                                          ) {
                                            return variant.color.toUpperCase();
                                          }
                                          // Default
                                          return "#000000";
                                        })()}
                                        onChange={(e) => {
                                          const hexColor =
                                            e.target.value.toUpperCase();
                                          // Convert hex to color name if possible, or use hex code
                                          const colorName =
                                            hexToColorName(hexColor);
                                          const finalColor =
                                            colorName || hexColor;
                                          handleVariantChange(
                                            actualIndex,
                                            "color",
                                            finalColor,
                                          );
                                          handleVariantChange(
                                            actualIndex,
                                            "colorCode",
                                            hexColor,
                                          );
                                          // Clear validation errors
                                          if (
                                            validationErrors[
                                              `variant_${actualIndex}_color`
                                            ]
                                          ) {
                                            setValidationErrors((prev) => {
                                              const newErrors = { ...prev };
                                              delete newErrors[
                                                `variant_${actualIndex}_color`
                                              ];
                                              delete newErrors[
                                                `variant_${actualIndex}_color_or_image`
                                              ];
                                              return newErrors;
                                            });
                                          }
                                        }}
                                        className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                                        title="Pick a color"
                                      />
                                    </div>
                                    {/* Text Input for Color Name/Code */}
                                    <input
                                      type="text"
                                      value={variant.color || ""}
                                      onChange={(e) => {
                                        const inputValue = e.target.value;
                                        handleVariantChange(
                                          actualIndex,
                                          "color",
                                          inputValue,
                                        );

                                        // Try to get hex code from color name or hex value
                                        let hexCode = null;
                                        if (
                                          inputValue.match(/^#[0-9A-Fa-f]{6}$/i)
                                        ) {
                                          // It's already a hex code
                                          hexCode = inputValue.toUpperCase();
                                        } else {
                                          // Try to convert color name to hex
                                          hexCode = colorNameToHex(inputValue);
                                        }

                                        // Update colorCode if we found a valid hex
                                        if (hexCode) {
                                          handleVariantChange(
                                            actualIndex,
                                            "colorCode",
                                            hexCode,
                                          );
                                        } else if (!inputValue) {
                                          // Reset to default if empty
                                          handleVariantChange(
                                            actualIndex,
                                            "colorCode",
                                            "#000000",
                                          );
                                        }

                                        // Clear validation errors when user types
                                        if (
                                          validationErrors[
                                            `variant_${actualIndex}_color`
                                          ]
                                        ) {
                                          setValidationErrors((prev) => {
                                            const newErrors = { ...prev };
                                            delete newErrors[
                                              `variant_${actualIndex}_color`
                                            ];
                                            delete newErrors[
                                              `variant_${actualIndex}_color_or_image`
                                            ];
                                            return newErrors;
                                          });
                                        }
                                      }}
                                      placeholder="e.g., Red, White, Blue or #FF0000"
                                      className={`flex-1 p-2 border rounded text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none ${
                                        validationErrors[
                                          `variant_${actualIndex}_color`
                                        ] ||
                                        validationErrors[
                                          `variant_${actualIndex}_color_or_image`
                                        ]
                                          ? "border-red-500"
                                          : "border-gray-300"
                                      }`}
                                    />
                                  </div>
                                  {validationErrors[
                                    `variant_${actualIndex}_color`
                                  ] && (
                                    <p className="text-red-500 text-xs mt-1">
                                      {
                                        validationErrors[
                                          `variant_${actualIndex}_color`
                                        ]
                                      }
                                    </p>
                                  )}
                                  {validationErrors[
                                    `variant_${actualIndex}_color_or_image`
                                  ] &&
                                    !validationErrors[
                                      `variant_${actualIndex}_color`
                                    ] && (
                                      <p className="text-red-500 text-xs mt-1">
                                        {
                                          validationErrors[
                                            `variant_${actualIndex}_color_or_image`
                                          ]
                                        }
                                      </p>
                                    )}
                                </div>
                              </div>

                              {/* MOQ */}
                              {/* <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            MOQ
                          </label>
                          <input
                            type="number"
                            value={variant.moq}
                            onChange={(e) =>
                              handleVariantMoqChange(index, e.target.value)
                            }
                            placeholder="MOQ (1-10000)"
                            min="1"
                            max="10000"
                            className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
                          />
                        </div> */}
                            </div>

                            {/* Product Images Upload Section */}
                            <div className="mb-4">
                              <div className="flex items-center justify-between mb-2">
                                <label className="block text-sm font-medium text-gray-700">
                                  Product Images (Max 9999){" "}
                                  <span className="text-red-500">*</span>
                                  <span className="text-xs text-gray-500 ml-2">
                                    (Multiple images - 450×450 required)
                                  </span>
                                </label>
                                {validationErrors[
                                  `variant_${actualIndex}_images`
                                ] && (
                                  <span className="text-red-500 text-xs">
                                    {
                                      validationErrors[
                                        `variant_${actualIndex}_images`
                                      ]
                                    }
                                  </span>
                                )}
                                <div className="flex gap-2">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleAddImageClick(actualIndex)
                                    }
                                    className="bg-blue-100 text-blue-700 font-semibold shadow-md hover:bg-blue-200 hover:text-blue-800 focus:bg-blue-200 focus:text-blue-800 focus:font-semibold focus:shadow-lg transition duration-200 px-3 py-1.5 rounded-lg text-sm"
                                    disabled={allImages.length >= 9999}
                                  >
                                    + Add Image
                                  </button>
                                  {/* Remove Color Button - Only show if there's more than one color in this variant */}
                                  {colorsInVariant.length > 1 && (
                                    <button
                                      type="button"
                                      onClick={() => removeColor(actualIndex)}
                                      className="bg-red-100 text-red-600 font-semibold shadow-md hover:bg-red-200 hover:text-red-700 focus:bg-red-200 focus:text-red-800 focus:font-semibold focus:shadow-lg transition duration-200 px-3 py-1.5 rounded-lg text-sm"
                                    >
                                      Remove Color
                                    </button>
                                  )}
                                </div>
                              </div>

                              <input
                                type="file"
                                multiple
                                accept="image/*"
                                style={{ display: "none" }}
                                ref={(el) =>
                                  (fileInputRefs.current[actualIndex] = el)
                                }
                                onChange={(e) =>
                                  handleColorImageChange(
                                    actualIndex,
                                    e.target.files,
                                  )
                                }
                              />

                              {/* Texture (optional) - checkbox, code, and image per color */}
                              <div className="mt-4 mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Texture (optional)
                                </label>
                                <div className="flex flex-wrap items-center gap-3">
                                  <label className="inline-flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={!!variant.texture}
                                      onChange={(e) =>
                                        handleVariantChange(
                                          actualIndex,
                                          "texture",
                                          e.target.checked,
                                        )
                                      }
                                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-400"
                                    />
                                    <span className="text-sm text-gray-700">
                                      Has texture
                                    </span>
                                  </label>
                                  <div className="flex items-center gap-2">
                                    <label className="text-sm text-gray-600 whitespace-nowrap">
                                      Texture code
                                    </label>
                                    <input
                                      type="text"
                                      value={variant.textureCode ?? ""}
                                      onChange={(e) =>
                                        handleVariantChange(
                                          actualIndex,
                                          "textureCode",
                                          e.target.value,
                                        )
                                      }
                                      placeholder="e.g. T-001"
                                      className="w-32 p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
                                    />
                                  </div>
                                  {variant.texture && (
                                    <>
                                      <input
                                        type="file"
                                        accept="image/*"
                                        style={{ display: "none" }}
                                        ref={(el) =>
                                          (textureImageInputRefs.current[
                                            actualIndex
                                          ] = el)
                                        }
                                        onChange={(e) => {
                                          const file = e.target.files?.[0];
                                          if (file)
                                            handleTextureImageChange(
                                              actualIndex,
                                              file,
                                            );
                                          e.target.value = "";
                                        }}
                                      />
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleAddTextureImageClick(
                                            actualIndex,
                                          )
                                        }
                                        className="bg-gray-100 text-gray-700 font-medium shadow hover:bg-gray-200 px-3 py-1.5 rounded-lg text-sm"
                                      >
                                        {variant.textureImageFile ||
                                        variant.existingTextureImage
                                          ? "Change texture image"
                                          : "Upload texture image"}
                                      </button>
                                      {(variant.textureImageFile ||
                                        variant.existingTextureImage) && (
                                        <div className="flex items-center gap-2">
                                          <img
                                            src={
                                              variant.textureImageFile
                                                ? URL.createObjectURL(
                                                    variant.textureImageFile,
                                                  )
                                                : variant.existingTextureImage
                                            }
                                            alt="Texture"
                                            className="w-12 h-12 object-cover rounded border"
                                            onError={(e) => {
                                              e.target.src = "/assets/logo.png";
                                              e.target.onerror = null;
                                            }}
                                          />
                                          <button
                                            type="button"
                                            onClick={() =>
                                              handleRemoveTextureImage(
                                                actualIndex,
                                              )
                                            }
                                            className="text-red-600 hover:text-red-800 text-sm"
                                          >
                                            Remove
                                          </button>
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>

                              {allImages.length > 0 && (
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mt-3">
                                  {allImages.map((file, imgIndex) => {
                                    const isExisting =
                                      imgIndex <
                                      (variant.existingImages?.length || 0);
                                    const src =
                                      typeof file === "string"
                                        ? file
                                        : URL.createObjectURL(file);
                                    const fileName =
                                      typeof file === "string"
                                        ? file.split("/").pop()
                                        : file.name;

                                    return (
                                      <div
                                        key={imgIndex}
                                        className="border rounded-xl p-2 flex flex-col items-center text-center bg-gray-50"
                                      >
                                        <img
                                          src={src}
                                          alt="preview"
                                          className="w-20 h-20 object-cover rounded mb-1"
                                          onError={(e) => {
                                            e.target.src = "/assets/logo.png";
                                            e.target.onerror = null;
                                          }}
                                        />
                                        <span className="text-xs text-gray-700 truncate w-full mb-1">
                                          {fileName}
                                        </span>
                                        {isExisting && (
                                          <span className="text-xs text-green-600 mb-1">
                                            Existing
                                          </span>
                                        )}
                                        <button
                                          type="button"
                                          className="text-xs text-red-600 hover:underline"
                                          onClick={() =>
                                            handleRemoveColorImage(
                                              actualIndex,
                                              isExisting
                                                ? imgIndex
                                                : imgIndex -
                                                    (variant.existingImages
                                                      ?.length || 0),
                                              isExisting,
                                            )
                                          }
                                        >
                                          Remove
                                        </button>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}

                      {/* Add Color to Variant Button (only in multi-variant mode) */}
                      {!formData.singleVarinet && (
                        <div className="mt-3 pt-3 border-t border-gray-300">
                          <button
                            type="button"
                            onClick={() => addColorToVariant(variantName)}
                            className="bg-green-100 text-green-700 font-semibold shadow-md hover:bg-green-200 hover:text-green-800 px-3 py-1.5 rounded-lg text-sm"
                          >
                            + Add Color to This Variant
                          </button>
                        </div>
                      )}
                    </div>
                  );
                });
              })()}

              {/* Add New Variant/Color Button */}
              <div className="mt-4 flex gap-2">
                {!formData.singleVarinet &&
                  (() => {
                    const groupedVariants = groupColorsByVariant(
                      formData.colors,
                    );
                    const variantCount = Object.keys(groupedVariants).length;
                    return variantCount < 9999;
                  })() && (
                    <button
                      type="button"
                      onClick={addVariant}
                      className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition duration-200"
                    >
                      + Add New Variant
                    </button>
                  )}
                {formData.singleVarinet && formData.colors.length < 9999 && (
                  <button
                    type="button"
                    onClick={() => addColor()}
                    className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition duration-200"
                  >
                    + Add Color
                  </button>
                )}
              </div>
              {!formData.singleVarinet &&
                (() => {
                  const groupedVariants = groupColorsByVariant(formData.colors);
                  const variantCount = Object.keys(groupedVariants).length;
                  return variantCount >= 9999;
                })() && (
                  <p className="text-sm text-gray-500 italic mt-2">
                    Maximum 9999 variants allowed
                  </p>
                )}
              {formData.singleVarinet && formData.colors.length >= 9999 && (
                <p className="text-sm text-gray-500 italic mt-2">
                  Maximum 9999 colors allowed
                </p>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end mt-4">
            <button
              type="button"
              onClick={handleClose}
              className="bg-gray-500 text-white px-4 py-2 rounded mr-2"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`px-4 py-2 rounded ${
                isSubmitting ||
                (Array.isArray(categories) && categories.length === 0)
                  ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                  : "bg-blue-500 text-white hover:bg-blue-600"
              }`}
              disabled={
                isSubmitting ||
                (Array.isArray(categories) && categories.length === 0)
              }
            >
              {isSubmitting
                ? "Processing..."
                : productId || productToEdit
                  ? "Update Product"
                  : "Add Product"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ProductModal;
